import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { SITE_CONFIG } from '@/data/siteData';
import { ArrowRight, Phone, Sparkles } from 'lucide-react';

const Hero = ({ 
  title, 
  subtitle, 
  description, 
  showCTA = true,
  cityName = null 
}) => {
  const { lang, t } = useLanguage();

  const defaultTitle = t(
    'Votre Partenaire Premium',
    'Your Premium Partner'
  );

  const defaultDescription = t(
    "Nous offrons des services de nettoyage professionnel, de gestion locative et de conciergerie sur mesure pour les propriétés prestigieuses de la Côte d'Azur. Notre engagement : excellence, discrétion et service irréprochable.",
    "We offer professional cleaning, property management and tailored concierge services for prestigious properties on the French Riviera. Our commitment: excellence, discretion and impeccable service."
  );

  return (
    <section 
      className="relative bg-white overflow-hidden"
      data-testid="hero-section"
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Gold Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Badge */}
          {cityName && (
            <div className="inline-flex items-center gap-2 bg-[#1a365d] rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-white text-sm font-medium">{cityName}</span>
            </div>
          )}

          {/* Subtitle */}
          <p className="text-[#D4AF37] font-semibold text-lg mb-4 tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {subtitle || SITE_CONFIG.companyName}
          </p>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-[#1a365d]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {title || defaultTitle}
          </h1>

          {/* Description */}
          <p className="text-slate-600 text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl">
            {description || defaultDescription}
          </p>

          {/* CTAs */}
          {showCTA && (
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] hover:from-[#C9A227] hover:to-[#B8960C] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                data-testid="hero-cta-quote"
              >
                {t('Obtenir une estimation rapide', 'Get a quick estimate')}
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href={SITE_CONFIG.phoneLink}
                className="inline-flex items-center justify-center gap-2 bg-[#1a365d] hover:bg-[#2d4a7c] text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg"
                data-testid="hero-cta-phone"
              >
                <Phone className="w-5 h-5" />
                {SITE_CONFIG.phone}
              </a>
            </div>
          )}
        </div>

        {/* Decorative Elements */}
        <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-96 h-96">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 to-transparent rounded-full blur-3xl" />
          <div className="absolute inset-8 border-2 border-[#D4AF37]/20 rounded-full" />
          <div className="absolute inset-16 border border-[#D4AF37]/10 rounded-full" />
        </div>
      </div>

      {/* Bottom Gold Border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
    </section>
  );
};

export default Hero;
